import { Clipboard } from '@capacitor/clipboard'
import { UntitledIcon } from '@faceup/icons'
import { ulCopy01 } from '@faceup/icons/ulCopy01'
import { Card, Prompt } from '@faceup/ui'
import { Button, Input, Space, Typography, useMessage } from '@faceup/ui-base'
import type { ChannelType } from '@faceup/utils'
import { lazy, Suspense as ReactSuspense, useContext, useState } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../../../../__generated__'
import { LayoutContext } from '../../../../../Contexts/LayoutContext'
import { ReportFormContext } from '../../../../../Contexts/ReportFormContext'
import { sharedMessages } from '../../../../../Shared/translations'
import { defineMessages, FormattedMessage, useIntl } from '../../../../../TypedIntl'
import useAnalytics from '../../../../../utils/analytics'
import { copyTextForOldBrowsers } from '../../../../../utils/helpers'
import { NotifyMe } from './NotifyMe'

const PDFReportPin = lazy(() => import('./PDFReportPin'))

const messages = defineMessages({
  pinPrompt: 'FollowUp.CreateReportForm.pinFormPrompt',
  pinLabel: 'FollowUp.CheckReport.pinLabel',
  pinCopy: 'Shared.action.copy',
  pinCopied: 'Shared.global.copied',
  checkReport: 'FollowUp.CheckReport.title',
  beforeLeaveDescription: 'FollowUp.ReportSent.beforeLeave.description',
  accessToReport: 'FollowUp.ReportSent.pinCard.title',
  pinCardDescription: 'FollowUp.ReportSent.pinCard.description',
})

const fragments = {
  PinForm_publicReportSource: graphql(`
    fragment PinForm_publicReportSource on PublicReportSource {
      id
      shortLink
      sourceType

      ...NotifyMe_publicReportSource
    }
  `),
}

type PinFormReportingProps = {
  reportSource: FragmentType<typeof fragments.PinForm_publicReportSource>
  shortLink?: never
  sourceType?: never
}

type PinFormSurveyProps = {
  shortLink: string
  sourceType: ChannelType
  reportSource?: never
}

type PinFormProps = (PinFormReportingProps | PinFormSurveyProps) & {
  isLoggedIn: boolean
}

function resolvePinFormProps(props: PinFormProps) {
  if ('reportSource' in props && props.reportSource) {
    const data = getFragmentData(fragments.PinForm_publicReportSource, props.reportSource)
    return { shortLink: data.shortLink, sourceType: data.sourceType, fragmentData: data }
  }
  return {
    shortLink: props.shortLink,
    sourceType: props.sourceType,
    fragmentData: null,
  }
}

export const PinForm = (props: PinFormProps) => {
  const { shortLink, sourceType, fragmentData } = resolvePinFormProps(props)
  const { isLoggedIn } = props
  const { formatMessage } = useIntl()
  const { victimPin, createdAt, tag } = useContext(ReportFormContext)
  const [pinPrompt, setPinPrompt] = useState(true)
  const { client } = useContext(LayoutContext)
  const message = useMessage()
  const { track } = useAnalytics()

  const savePinToClipboard = async () => {
    track('Report pin copied')
    // if client is JOBKA, they are unable to copy PIN in webview, that means we need to call a custom function, which they inject into the page
    if (client === 'jobka') {
      if (window?.copyToClipboard) {
        // JOBKA custom function - declaration in `typings.d.ts`
        window?.copyToClipboard({ Text: victimPin ?? '' })
        message.success(formatMessage(messages.pinCopied))
        setPinPrompt(false)
        return
      }
      console.error('Function is not implemented')
    }

    try {
      await Clipboard.write({ string: victimPin ?? '' })
      message.success(formatMessage(messages.pinCopied))
      setPinPrompt(false)
    } catch {
      const copied = copyTextForOldBrowsers(victimPin ?? '')
      if (copied) {
        message.success(formatMessage(messages.pinCopied))
        setPinPrompt(false)
      } else {
        message.error(formatMessage(sharedMessages.apiError))
      }
    }
  }

  return (
    <Card className='overflow-hidden border border-colorBorder !shadow-none'>
      <div className='flex flex-col gap-6 p-8 bg-colorPrimaryBg'>
        <Typography.Title level={4} className='text-center !m-0'>
          <FormattedMessage {...messages.accessToReport} />
        </Typography.Title>
        <Typography.Text className='text-center block'>
          <FormattedMessage {...messages.pinCardDescription} />
        </Typography.Text>
        <div className='flex flex-col sm:flex-row gap-2'>
          <div className='flex-1'>
            <Input
              size='large'
              readOnly
              value={victimPin ?? ''}
              className='text-center sensitive'
            />
          </div>
          <Button
            size='large'
            data-test='report-sent-pin-copy'
            onClick={() => savePinToClipboard()}
          >
            <Space>
              <UntitledIcon icon={ulCopy01} />
              <FormattedMessage {...messages.pinCopy} />
            </Space>
          </Button>
          <ReactSuspense
            fallback={
              <Button type='primary' size='large' loading>
                <FormattedMessage {...sharedMessages.download} />
              </Button>
            }
          >
            <PDFReportPin
              pin={victimPin}
              createdAt={createdAt}
              shortId={shortLink}
              tag={tag ?? ''}
              onClick={() => {
                track('Report pin downloaded')
                setPinPrompt(false)
              }}
              channelType={sourceType}
              size='large'
            />
          </ReactSuspense>
        </div>
        {fragmentData && (
          <NotifyMe
            publicReportSource={fragmentData}
            reportPin={victimPin ?? ''}
            isLoggedIn={isLoggedIn}
          />
        )}
        <Prompt when={pinPrompt} message={formatMessage(messages.pinPrompt)} />
      </div>
    </Card>
  )
}
